import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, Play, Upload, Flame, Sparkles, X, Trophy, Vote, Loader2 } from "lucide-react";
import { api, fileStreamUrl } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import { useCategories } from "../lib/useCategories";
import { CategoryIcon } from "../components/CategoryIcon";
import { TopMadaTeaser } from "../components/TopMadaTeaser";
import TrackCard from "../components/TrackCard";
import ArtistCard from "../components/ArtistCard";
import { PersonalHero } from "../components/PersonalHero";
import { ChallengeBlock } from "../components/ChallengeBlock";
import { AdSenseSlot } from "../components/AdSenseSlot";
import { useAuth } from "../context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1760410334929-24052fc69765?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwbXVzaWMlMjBjb25jZXJ0JTIwZW5lcmd5fGVufDB8fHx8MTc3NjQ5MTU3Nnww&ixlib=rb-4.1.0&q=85";

export const HomePage = () => {
  const { user } = useAuth();
  const [trending, setTrending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [artists, setArtists] = useState([]);
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState({ artists: null, tracks: null, countries: null, genres: null });
  const { cats: allCats } = useCategories();
  // En mode déconnecté on cache les catégories désactivées (même celles en "Bientôt")
  // pour ne pas exposer aux visiteurs des sections pas encore prêtes.
  const dynamicCats = user ? allCats : allCats.filter(c => c.is_active);
  const [texts, setTexts] = useState({
    "hero.badge1": "Nouvelle plateforme pour artistes africains",
    "hero.badge2": "100% gratuit",
    "hero.title_prefix": "Les talents de",
    "hero.title_accent": "Madagascar",
    "hero.title_suffix": "et d'Afrique.",
    "hero.subtitle": "Voamada rassemble musiciens, humoristes, danseurs, cinéastes, artistes visuels, stylistes, créateurs. Un tremplin pour tous les talents du continent.",
    "hero.cta_primary": "Déposer ma graine",
    "hero.cta_ghost": "Explorer les artistes",
  });

  useEffect(() => {
    Promise.all([
      api.get("/tracks", { params: { sort: "trending", limit: 8 } }),
      api.get("/tracks", { params: { sort: "recent", limit: 12 } }),
      api.get("/artists", { params: { limit: 12 } }),
      api.get("/ads", { params: { placement: "banner", active_only: true } }),
    ]).then(([t, r, a, d]) => {
      setTrending(t.data); setRecent(r.data); setArtists(a.data); setAds(d.data);
    }).catch(() => {});
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/site-texts").then(({ data }) => setTexts((prev) => ({ ...prev, ...data }))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Bannière promo Shorts (dismissible, mémorisé en localStorage) */}
      <ShortsLaunchBanner />

      {/* Widget Top Mada — top 3 cette semaine */}
      <TopMadaTopWidget />
      {/* Personal welcome (artist studio or visitor hub) — connecté uniquement */}
      <PersonalHero />

      {/* HERO public — uniquement si non connecté */}
      {!user && (
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-dark/70 to-brand-dark" />

        <div className="relative px-6 md:px-12 pt-16 pb-24 max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-6">
            {texts["hero.badge1"] && (
              <span className="inline-flex items-center gap-2 tag-pill border-brand-red/40 bg-brand-red/10 text-brand-red">
                <Flame size={12} /> {texts["hero.badge1"]}
              </span>
            )}
            {texts["hero.badge2"] && (
              <span className="inline-flex items-center gap-2 tag-pill border-[#0B8B47]/40 bg-[#0B8B47]/10 text-[#12A859]">
                ✓ {texts["hero.badge2"]}
              </span>
            )}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 text-balance max-w-4xl">
            {texts["hero.title_prefix"]}{" "}
            <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#0B8B47] bg-clip-text text-transparent">
              {texts["hero.title_accent"]}
            </span>
            {texts["hero.title_suffix"] && <> {texts["hero.title_suffix"]}</>}
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mb-10 text-balance">
            {texts["hero.subtitle"]}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/upload" className="btn-primary inline-flex items-center gap-2" data-testid="hero-upload-btn">
              <Upload size={18} /> {texts["hero.cta_primary"]}
            </Link>
            <Link to="/explore" className="btn-ghost inline-flex items-center gap-2" data-testid="hero-explore-btn">
              <Play size={16} fill="currentColor" /> {texts["hero.cta_ghost"]}
            </Link>
          </div>

          {/* stats */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
            {[
              { k: "Artistes", v: stats.artists ?? "—" },
              { k: "Morceaux", v: stats.tracks ?? "—" },
              { k: "Pays", v: stats.countries ? `${stats.countries}+` : "—" },
              { k: "Genres", v: stats.genres ? `${stats.genres}+` : "—" },
            ].map((s) => (
              <div key={s.k} className="border border-white/10 rounded-xl p-4 bg-white/[0.02]" data-testid={`home-stat-${s.k.toLowerCase()}`}>
                <p className="font-display text-3xl font-black text-brand-red">{s.v}</p>
                <p className="text-xs uppercase tracking-widest text-white/50 mt-1">{s.k}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* TOP MADA TEASER (vidéos + countdown) */}
      <TopMadaTeaser />

      {/* CATEGORIES SHOWCASE */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-16 reveal-on-scroll">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">Toutes les catégories</p>
            <h2 className="font-display text-3xl md:text-4xl font-black">Explorez les talents</h2>
          </div>
          <Link to="/about" className="text-sm text-brand-red hover:text-brand-redHover inline-flex items-center gap-1">
            Notre vision <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          {dynamicCats.map((c) => {
            const isComingSoon = !c.is_active && !!c.coming_soon;
            const Tag = isComingSoon ? "div" : Link;
            const tagProps = isComingSoon
              ? { className: "" }
              : { to: `/explore?category=${c.key}` };
            return (
            <Tag
              key={c.key}
              {...tagProps}
              className={`relative aspect-[4/3] md:aspect-square rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br ${c.color} bg-opacity-20 group p-5 flex flex-col justify-between transition-transform duration-300 ${isComingSoon ? "opacity-80 cursor-default" : "hover:-translate-y-1"}`}
              data-testid={`category-tile-${c.key}`}
            >
              <div className="absolute inset-0 bg-brand-dark/40 group-hover:bg-brand-dark/20 transition-colors" />
              {isComingSoon && (
                <span className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-amber-500/90 text-brand-dark text-[10px] font-bold tracking-wide">
                  BIENTÔT · {c.coming_soon}
                </span>
              )}
              <div className="relative transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-rotate-6 group-hover:-translate-y-1">
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/20 blur-2xl rounded-full transition-all duration-500" aria-hidden />
                <CategoryIcon catKey={c.key} emoji={c.emoji} size={64} className="relative drop-shadow-2xl group-hover:drop-shadow-[0_8px_25px_rgba(255,200,80,0.6)]" />
              </div>
              <div className="relative">
                <p className="font-display font-black text-xl md:text-2xl leading-tight">{c.label}</p>
                <p className="text-xs text-white/80 mt-1 inline-flex items-center gap-1">
                  {isComingSoon ? `Disponible ${c.coming_soon}` : (<>Explorer <ArrowRight size={10} /></>)}
                </p>
              </div>
            </Tag>
            );
          })}
        </div>
      </section>

      {/* DÉFI CUISINE DU MOIS */}
      <ChallengeBlock />

      {/* GOOGLE ADSENSE — Bannière Home (s'affiche uniquement après approbation + activation admin) */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto mb-10">
        <AdSenseSlot slotKey="homeBanner" format="auto" minHeight={120} testid="ad-home-banner" />
      </section>

      {/* AD BANNER */}      {ads.length > 0 && (
        <section className="px-6 md:px-12 max-w-7xl mx-auto -mt-8 mb-12">
          <a
            href={ads[0].link_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => api.post(`/ads/${ads[0].ad_id}/click`)}
            className="block rounded-2xl border border-brand-orange/30 bg-gradient-to-r from-brand-red/10 via-brand-orange/10 to-brand-yellow/10 p-5 hover:border-brand-red/60 transition-colors"
            data-testid="home-ad-banner"
          >
            <p className="text-[10px] tracking-widest text-brand-yellow uppercase font-bold mb-1">Sponsorisé</p>
            <p className="font-display text-xl font-bold">{ads[0].title}</p>
            <p className="text-sm text-white/70">{ads[0].description}</p>
          </a>
        </section>
      )}

      {/* TRENDING */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-16 reveal-on-scroll">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">En tendance</p>
            <h2 className="font-display text-3xl md:text-4xl font-black">Ça vibre cette semaine</h2>
          </div>
          <Link to="/explore" className="text-sm text-brand-red hover:text-brand-redHover inline-flex items-center gap-1">
            Tout voir <ArrowRight size={14} />
          </Link>
        </div>
        {trending.length === 0 ? (
          <EmptyBlock label="Aucun morceau pour l'instant." />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6 stagger">
            {trending.map((t) => <TrackCard key={t.track_id} track={t} list={trending} />)}
          </div>
        )}
      </section>

      {/* ARTISTS */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-16 reveal-on-scroll">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">À découvrir</p>
            <h2 className="font-display text-3xl md:text-4xl font-black">Artistes du continent</h2>
          </div>
          <Link to="/artists" className="text-sm text-brand-red hover:text-brand-redHover inline-flex items-center gap-1">
            Tous les artistes <ArrowRight size={14} />
          </Link>
        </div>
        {artists.length === 0 ? (
          <EmptyBlock label="Les premiers artistes arrivent bientôt." />
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {artists.slice(0, 6).map((a) => <ArtistCard key={a.user_id} artist={a} />)}
          </div>
        )}
      </section>

      {/* PARTNER BANNER */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-16 reveal-on-scroll">
        <div
          className="relative overflow-hidden rounded-3xl border border-brand-yellow/20 p-8 md:p-12 bg-gradient-to-br from-brand-red/15 via-brand-orange/10 to-brand-yellow/15"
          data-testid="home-partner-banner"
        >
          <div
            className="absolute inset-0 opacity-30 pointer-events-none mix-blend-overlay"
            style={{
              backgroundImage:
                "radial-gradient(1px 1px at 30% 40%, rgba(255,255,255,0.35), transparent), radial-gradient(1.2px 1.2px at 70% 60%, rgba(255,255,255,0.25), transparent), radial-gradient(1px 1px at 50% 85%, rgba(255,255,255,0.2), transparent)",
              backgroundSize: "220px 220px, 180px 180px, 260px 260px",
            }}
          />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-brand-yellow font-bold mb-3">
                Devenez partenaire officiel
              </p>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-3 text-balance max-w-2xl">
                Associez votre marque à la nouvelle voix de l'Afrique.
              </h3>
              <p className="text-white/70 max-w-xl">
                Sponsoring, médias, festivals, labels — rejoignez les premiers
                partenaires officiels de Voamada.
              </p>
            </div>
            <Link
              to="/partners"
              className="btn-primary inline-flex items-center gap-2 shrink-0"
              data-testid="home-partner-cta"
            >
              Devenir partenaire <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* RECENT */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-32 reveal-on-scroll">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2">Nouveautés</p>
            <h2 className="font-display text-3xl md:text-4xl font-black">Dernières sorties</h2>
          </div>
        </div>
        {recent.length === 0 ? (
          <EmptyBlock label="Soyez le premier à uploader un morceau !" cta />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-6 stagger">
            {recent.map((t) => <TrackCard key={t.track_id} track={t} list={recent} />)}
          </div>
        )}
      </section>
    </div>
  );
};

const EmptyBlock = ({ label, cta }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
    <p className="text-white/60 mb-4">{label}</p>
    {cta && (
      <Link to="/upload" className="btn-primary inline-flex items-center gap-2">
        <Upload size={16} /> Uploader maintenant
      </Link>
    )}
  </div>
);

const ShortsLaunchBanner = () => {
  const [visible, setVisible] = useState(false);
  const [thumbs, setThumbs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [freshCount, setFreshCount] = useState(0);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem("shortsBannerDismissed_v1");
      if (!dismissed) setVisible(true);
    } catch (_) {
      setVisible(true);
    }
  }, []);

  // Charge un mini-feed pour les vignettes (1ère vidéo trending)
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    api
      .get("/shorts", { params: { per_page: 8 } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.shorts) ? data.shorts : [];
        const list = rows
          .map((s) => {
            const cover = s.cover_path ? fileStreamUrl(s.cover_path) : null;
            const fallback = s.user_picture || s.artist_picture || null;
            return {
              src: cover || fallback,
              title: s.title || s.artist_name || "",
            };
          })
          .filter((t) => !!t.src)
          .slice(0, 6);
        setThumbs(list);
        setFreshCount(Number(data?.count_last_7d || 0));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Rotation toutes les 5s
  useEffect(() => {
    if (thumbs.length < 2) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % thumbs.length);
    }, 5000);
    return () => clearInterval(t);
  }, [thumbs.length]);

  const dismiss = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      localStorage.setItem("shortsBannerDismissed_v1", "1");
    } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  const current = thumbs[idx];

  return (
    <div
      data-testid="shorts-launch-banner"
      className="relative overflow-hidden border-b border-fuchsia-500/30 bg-gradient-to-r from-fuchsia-600/20 via-purple-600/20 to-pink-600/20"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-screen"
           style={{ background: "radial-gradient(600px 120px at 20% 50%, rgba(217,70,239,0.35), transparent), radial-gradient(600px 120px at 80% 50%, rgba(236,72,153,0.35), transparent)" }} />
      <Link
        to="/shorts"
        data-testid="shorts-launch-banner-link"
        className="relative flex items-center justify-between gap-3 px-4 py-2.5 sm:px-6 sm:py-3 text-white hover:opacity-95 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          {current ? (
            <span
              data-testid="shorts-launch-banner-thumb"
              className="relative shrink-0 w-9 h-12 sm:w-10 sm:h-14 rounded-md overflow-hidden ring-1 ring-white/30 shadow-lg shadow-fuchsia-900/30 bg-black/40"
            >
              <img
                key={idx}
                src={current.src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover animate-fade-up"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-5 h-5 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                  <Play size={10} className="text-white translate-x-[1px]" fill="currentColor" />
                </span>
              </span>
              {/* indicateurs (dots) */}
              {thumbs.length > 1 && (
                <span className="absolute bottom-0.5 left-0 right-0 flex justify-center gap-0.5">
                  {thumbs.map((_, i) => (
                    <span
                      key={i}
                      className={`block w-1 h-1 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`}
                    />
                  ))}
                </span>
              )}
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 backdrop-blur shrink-0">
              <Sparkles size={14} className="text-fuchsia-200" />
            </span>
          )}
          <span className="text-sm sm:text-base font-medium truncate">
            <span className="inline-flex items-center gap-1.5 mr-2 px-2 py-0.5 rounded-full bg-white/20 text-[10px] sm:text-xs font-semibold uppercase tracking-wide">
              <Flame size={11} /> Nouveau
            </span>
            <span className="hidden sm:inline">Section </span>
            <span className="font-bold">Shorts</span> dispo&nbsp;!
            {freshCount > 0 ? (
              <span
                data-testid="shorts-launch-banner-fresh"
                className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-pink-500/30 ring-1 ring-pink-300/40 text-[10px] sm:text-xs font-semibold whitespace-nowrap align-middle"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-pink-300 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pink-200" />
                </span>
                {freshCount} nouveau{freshCount > 1 ? "x" : ""} cette semaine
              </span>
            ) : (
              <span className="hidden md:inline text-white/80"> &nbsp;Découvre des vidéos verticales d'artistes en 1 clic.</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline-flex items-center gap-1 text-xs sm:text-sm font-semibold bg-white text-fuchsia-700 px-3 py-1.5 rounded-full hover:bg-white/90">
            Explorer <ArrowRight size={14} />
          </span>
          <button
            type="button"
            onClick={dismiss}
            data-testid="shorts-launch-banner-dismiss"
            aria-label="Fermer"
            className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/15 transition"
          >
            <X size={16} />
          </button>
        </div>
      </Link>
    </div>
  );
};

export default HomePage;

/** Widget Top Mada · top 3 clips cette semaine — affiché sous la bannière Shorts sur la HomePage */
const TopMadaTopWidget = () => {
  const { user } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null);
  const isAuthed = !!user;

  const refresh = () =>
    api.get("/topmada/videos")
      .then(({ data }) => {
        const sorted = [...(data.videos || [])].sort((a, b) => (b.votes || 0) - (a.votes || 0)).slice(0, 3);
        setClips(sorted);
      })
      .catch(() => {});

  useEffect(() => {
    let cancelled = false;
    refresh().finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const vote = async (videoId) => {
    if (!isAuthed) return;
    setVoting(videoId);
    try {
      const { data } = await api.post("/topmada/vote", {
        target_type: "topmada_video",
        target_id: videoId,
        category_slug: "topmada_clip",
      });
      if (!data.already_voted) {
        setClips((cur) =>
          cur.map((c) => c.video_id === videoId ? { ...c, has_voted: true, votes: (c.votes || 0) + 1 } : c)
            .sort((a, b) => (b.votes || 0) - (a.votes || 0))
        );
      }
    } catch (_) { /* ignore */ } finally { setVoting(null); }
  };

  if (loading || clips.length === 0) return null;

  const podiumColors = ["text-amber-300", "text-zinc-200", "text-orange-400"];

  return (
    <section data-testid="topmada-top3-widget" className="border-b border-amber-400/15 bg-gradient-to-r from-amber-500/[0.06] via-orange-500/[0.04] to-amber-500/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-5">
        <Link to="/topmada" className="shrink-0 inline-flex items-center gap-2 group">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-400/15 ring-1 ring-amber-300/40 group-hover:bg-amber-400/25 transition">
            <Trophy size={18} className="text-amber-300" />
          </span>
          <span className="hidden sm:block">
            <span className="block text-xs uppercase tracking-wider text-amber-300 font-bold">Top Mada</span>
            <span className="block text-[11px] text-white/55 -mt-0.5">Clips de la semaine</span>
          </span>
        </Link>

        <div className="flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar">
          {clips.map((c, i) => {
            const rank = i + 1;
            return (
              <div
                key={c.video_id}
                data-testid={`topmada-widget-row-${rank}`}
                className="shrink-0 flex items-center gap-2.5 pl-1.5 pr-2.5 py-1.5 rounded-full bg-white/[0.04] border border-white/10"
              >
                <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-extrabold bg-black/40 ${podiumColors[i]}`}>
                  {rank}
                </span>
                {c.thumbnail ? (
                  <img src={c.thumbnail.startsWith("http") ? c.thumbnail : fileStreamUrl(c.thumbnail)} alt="" className="w-8 h-8 rounded-md object-cover ring-1 ring-amber-300/40" />
                ) : (
                  <span className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center text-[10px] text-white/40">▶</span>
                )}
                <Link
                  to="/topmada"
                  className="text-sm font-bold text-white hover:text-amber-200 truncate max-w-[120px] sm:max-w-[160px]"
                  title={c.title}
                >
                  {c.title}
                </Link>
                <span className="hidden sm:inline text-[11px] text-amber-300 font-semibold whitespace-nowrap">{c.votes} vote{c.votes > 1 ? "s" : ""}</span>
                <button
                  onClick={() => vote(c.video_id)}
                  disabled={!isAuthed || c.has_voted || voting === c.video_id}
                  data-testid={`topmada-widget-vote-${c.video_id}`}
                  title={!isAuthed ? "Connecte-toi pour voter" : c.has_voted ? "Déjà voté aujourd'hui" : "Voter"}
                  className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold transition ${
                    !isAuthed
                      ? "bg-white/5 text-white/30 cursor-not-allowed"
                      : c.has_voted
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40"
                      : "bg-gradient-to-r from-amber-300 to-orange-400 text-black hover:brightness-105"
                  }`}
                >
                  {voting === c.video_id
                    ? <Loader2 size={11} className="animate-spin" />
                    : c.has_voted ? "✓" : <><Vote size={11} /> Voter</>}
                </button>
              </div>
            );
          })}
        </div>

        <Link
          to="/topmada"
          data-testid="topmada-widget-see-all"
          className="hidden sm:inline-flex items-center gap-1 shrink-0 text-xs font-bold text-amber-200 hover:text-amber-100 whitespace-nowrap"
        >
          Voir tout <ArrowRight size={12} />
        </Link>
      </div>
    </section>
  );
};
